import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useMutation } from '@apollo/client/react';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { EscrowMemberRemoveDocument } from 'src/graphql/actions/escrowMembers/escrowMemberRemove.mutation';
import { Button } from '@/components/ui/button';

type Props = {
  id: string;
  successCallback?: () => void;
};

const RemoveMemberDialog = ({ id, successCallback }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [removeEscrowMember, { loading }] = useMutation(EscrowMemberRemoveDocument);

  const onRemove = async () => {
    try {
      await removeEscrowMember({
        variables: {
          escrowMemberId: id,
        },
      });

      setIsOpen(false);
      toast.success('Escrow member removed!', {
        description: 'They will no longer have access to this escrow.',
      });
      successCallback?.();
    } catch (error) {
      toast.error('Failed to remove member access');
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <Trash2 className="text-destructive" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure you want to remove this person as an escrow member?</AlertDialogTitle>
          <AlertDialogDescription>
            They will no longer be able to see, or interact with, this escrow. If you want to limit their permissions,
            you can set them as a viewer instead.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={onRemove} disabled={loading} isLoading={loading}>
            Remove
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default RemoveMemberDialog;
